import type { Dispatch, JSX, ReactNode, RefObject, SetStateAction } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const NAV_MINIMIZED_STORAGE_KEY = 'feathr:nav:isMinimized';
const NAV_WIDTH_EXPANDED = 240;
const NAV_WIDTH_MINIMIZED = 64;

type TSecondaryNav = 'home' | 'projects' | 'community' | 'settings' | null;
type TAnimationDirection = 'left' | 'right';
type NavigationProviderProps = { readonly children: ReactNode };

interface INavigationContext {
  isMinimized: boolean;
  activeSecondaryNav: TSecondaryNav;
  navWidth: number;
  isAnimating: boolean;
  animationDirection: TAnimationDirection;
  scrollableRef: RefObject<HTMLDivElement>;
  setIsMinimized: Dispatch<SetStateAction<boolean>>;
  setActiveSecondaryNav: (nav: TSecondaryNav) => void;
  setIsAnimating: Dispatch<SetStateAction<boolean>>;
  toggleMinimized: () => void;
}

const NavigationContext = createContext<INavigationContext | undefined>(undefined);

function getInitialMinimizedState(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const stored = localStorage.getItem(NAV_MINIMIZED_STORAGE_KEY);
  return stored === 'true';
}

function getSecondaryNavFromPath(pathname: string): TSecondaryNav {
  const pathSegments = pathname.split('/');
  const primarySegment = pathSegments[2] || '';

  if (['accounts', 'dashboard', 'marketing'].includes(primarySegment)) {
    return 'home';
  }
  if (primarySegment === 'projects' || primarySegment === 'project') {
    return 'projects';
  }
  if (primarySegment === 'data') {
    return 'community';
  }
  if (primarySegment === 'settings') {
    return 'settings';
  }
  return null;
}

function getInitialPathname(): string {
  if (typeof window !== 'undefined') {
    return window.location.pathname;
  }
  return '';
}

function NavigationProvider({ children }: NavigationProviderProps): JSX.Element {
  const [isMinimized, setIsMinimized] = useState<boolean>(getInitialMinimizedState);
  const [activeSecondaryNav, setActiveSecondaryNav] = useState<TSecondaryNav>(() =>
    getSecondaryNavFromPath(getInitialPathname()),
  );
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [animationDirection, setAnimationDirection] = useState<TAnimationDirection>('right');
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(NAV_MINIMIZED_STORAGE_KEY, String(isMinimized));
  }, [isMinimized]);

  const toggleMinimized = (): void => {
    setIsMinimized((prev) => !prev);
  };

  const handleSecondaryNavChange = useCallback(
    (nav: TSecondaryNav): void => {
      if (isAnimating) {
        return;
      }

      setAnimationDirection(nav === null ? 'left' : 'right');
      setIsAnimating(true);
      setActiveSecondaryNav(nav);
    },
    [isAnimating],
  );

  const navWidth = isMinimized ? NAV_WIDTH_MINIMIZED : NAV_WIDTH_EXPANDED;

  const memoizedValue = useMemo(() => {
    return {
      isMinimized,
      activeSecondaryNav,
      navWidth,
      isAnimating,
      animationDirection,
      scrollableRef,
      setIsMinimized,
      setActiveSecondaryNav: handleSecondaryNavChange,
      setIsAnimating,
      toggleMinimized,
    };
  }, [
    isMinimized,
    activeSecondaryNav,
    navWidth,
    isAnimating,
    animationDirection,
    handleSecondaryNavChange,
  ]);

  return <NavigationContext.Provider value={memoizedValue}>{children}</NavigationContext.Provider>;
}

function useNavigation(): INavigationContext {
  const context = useContext(NavigationContext);

  if (context === undefined) {
    throw new Error('useNavigation hook must be wrapped in a NavigationProvider context provider');
  }
  return context;
}

export { NavigationProvider, useNavigation, NAV_WIDTH_EXPANDED, NAV_WIDTH_MINIMIZED };
export type { TSecondaryNav, TAnimationDirection };
